import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import DialogComponent from 'components/Dialog';
import {isEmpty} from 'lodash';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {DialogTypes} from 'common/names';
import {DiplomatAdminUser, IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {enums} from 'types/enums';
import {IChangePassword} from 'types/PasswordData';
import Grid from '@mui/material/Grid';

import FormikTextField from 'components/inputs/FormikTextField';
import FormikDropdown from 'components/inputs/FormikDropdown';
import Button from 'components/Button';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {ChangePasswordForm} from 'features/login/ChangePasswordForm';
import {layoutSelectors} from 'features/layout/redux';
import {useDispatch, useSelector} from 'react-redux';
import {castToSend} from 'common/yup/castToSend';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFormContext} from 'react-hook-form';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {SetValueOptions} from 'components/hook-form/helpers';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikMultiselect from 'components/inputs/FormikMultiselect';
import {settingsSelectors, settingsActions} from 'redux/settings';
import {GridCentered} from 'components/GridCentered';
import {licenseSelectors} from 'redux/license';
import {DiplomatFeature} from 'types/DiplomatFeature';

type Props = {
   onClose: () => void;
   onSubmit: (
      values: IDiplomatAdminUser,
      formikBag: FormOnSubmitHelpers<IDiplomatAdminUser>
   ) => void;
   user?: IDiplomatAdminUser;
};

const AddEditUserForm = ({
   onClose,
   user,
   isPassOpen,
   openPass,
}: {
   onClose: () => void;
   user?: IDiplomatAdminUser;
   isPassOpen: boolean;
   openPass: (b: boolean) => void;
}) => {
   const {t} = useTranslation();
   const passwordData = useSelector(layoutSelectors.passwordData);
   const roles = useSelector(settingsSelectors.adminRoles);
   const dispatch = useDispatch();
   const {setValue} = useFormContext<IDiplomatAdminUser>();
   const isLdap = useFieldValue<IDiplomatAdminUser>('ldapPasswordAuthentication') as boolean;
   const license = useSelector(licenseSelectors.getLicense);
   const isEnterprise = license.edition === enums.LicenseEdition.enum.ENTERPRISE;
   const isProfessional = license.edition === enums.LicenseEdition.enum.PROFESSIONAL;
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);

   React.useEffect(() => {
      !roles && dispatch(settingsActions.fetchAdminRoles);
   }, [dispatch, roles]);

   return (
      <>
         <Grid container spacing={1}>
            <Grid item xs={12}>
               <FormikTextField
                  autoFocus
                  name="contactName"
                  placeholder={t('Contact Name')}
                  disabled={isLdap}
               />
            </Grid>
            <Grid item xs={7}>
               <FormikMultiselect name="adminRoleIds" placeholder={t('Roles')} options={roles} />
            </Grid>
            {isFeatureSupported(DiplomatFeature.ADMIN_TOTP) && (
               <GridCentered item xs={5}>
                  <FormikCheckbox
                     name="totpRequired"
                     label={t('Require TOTP for two-factor authentication (TOTP)')}
                  />
               </GridCentered>
            )}
            <Grid item xs={12}>
               <FormikTextField
                  name="userName"
                  placeholder={t('Username')}
                  disabled={!isEmpty(user)}
               />
            </Grid>
            <Grid item xs={4}>
               <Button type="button" fullWidth onClick={() => openPass(true)} disabled={isLdap}>
                  {user ? t('Reset Password') : t('Set Password')}
               </Button>
            </Grid>
            {/* <Grid item xs={12}>
                  <FormikTextField name="domain" placeholder={t('Domain')} />
               </Grid>
               <Grid item xs={12}>
                  <FormikTextField name="userId" placeholder={t('User ID')} />
               </Grid>
               <Grid item xs={12}>
                  <FormikCheckbox
                     name="twoFactorAuthentication"
                     label={t('Require Two-Factor Authentication')}
                  />
               </Grid> 
               <Grid item xs={12}>
                  <Typography>{t('* Required field')}</Typography>
               </Grid>*/}
            <FormFooterButtons cancel={onClose} skipPermissionCheck />
            {/* {JSON.stringify(formikProps.errors)} */}
         </Grid>

         {isPassOpen ? (
            <DialogComponent
               maxWidth="md"
               header={DialogTypes.SetPassword.dialogName}
               close={() => openPass(false)}
            >
               <ChangePasswordForm
                  onSubmit={(
                     values: IChangePassword,
                     formikHelpers: FormOnSubmitHelpers<IChangePassword>
                  ) => {
                     setValue('password', values.newPassword, SetValueOptions);
                     setValue('lastPasswordUpdate', new Date(), SetValueOptions);
                     formikHelpers.setSubmitting(false);
                     openPass(false);
                  }}
                  onClose={() => openPass(false)}
                  fromUserAdmin={true}
                  passwordData={passwordData}
                  lastPasswordUpdate={user?.lastPasswordUpdate}
               />
            </DialogComponent>
         ) : null}
      </>
   );
};

const AddEditUserDialog = (props: Props) => {
   const {onClose, onSubmit: _onSubmit, user} = props;

   const [isPassOpen, openPass] = useState(false);

   const onSubmit = React.useCallback(
      (values: IDiplomatAdminUser, helpers: FormOnSubmitHelpers<IDiplomatAdminUser>) =>
         _onSubmit(castToSend(DiplomatAdminUser, values), helpers),
      [_onSubmit]
   );

   return (
      <DialogComponent
         maxWidth="sm"
         header={
            user ? DialogTypes.EditUserAccount.dialogName : DialogTypes.AddUserAccount.dialogName
         }
         close={onClose}
      >
         <Formik
            initialValues={DiplomatAdminUser.cast(user) as IDiplomatAdminUser}
            validationSchema={DiplomatAdminUser}
            onSubmit={onSubmit}
         >
            <AddEditUserForm
               onClose={onClose}
               user={user}
               isPassOpen={isPassOpen}
               openPass={openPass}
            />
         </Formik>
         {user && !isEmpty(user) ? <LastUpdatedLabel date={user?.timestamp} /> : null}
      </DialogComponent>
   );
};

export default React.memo(AddEditUserDialog);
